import { onDocumentReady, prefersReducedMotion } from '@theme/utilities';

/**
 * Scroll-triggered reveal animations.
 *
 * Sections fade and slide in as they enter the viewport. Explicit `[data-reveal]`
 * targets are revealed the same way, and `[data-reveal-stagger]` containers reveal
 * their children one after another. Everything is gated behind
 * `prefers-reduced-motion` and disabled in the theme editor so content is never
 * hidden from the merchant while designing.
 *
 * Only elements fully below the fold at init get the hidden state, so nothing
 * above the fold ever flashes. Once the animation finishes the helper classes are
 * removed, leaving elements in their natural state (so card hover transitions,
 * sticky positioning, etc. keep working normally).
 */

const HIDDEN_CLASS = 'will-reveal';
const REVEALED_CLASS = 'is-revealed';
const STAGGER_DELAY = 0.09;

/**
 * Whether the element's top edge is below the visible viewport.
 * @param {Element} element
 * @returns {boolean}
 */
function isBelowFold(element) {
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
  return element.getBoundingClientRect().top > viewportHeight;
}

/**
 * Remove the reveal classes once the transition has finished, so the element
 * returns to its natural state.
 * @param {Element} element
 */
function cleanupClasses(element) {
  element.classList.remove(HIDDEN_CLASS, REVEALED_CLASS);
}

/**
 * Reveal an element and clean up after the transition completes.
 * @param {Element} element
 */
function reveal(element) {
  element.classList.add(REVEALED_CLASS);

  const onEnd = (event) => {
    if (event.target !== element) return;
    if (event.propertyName !== 'transform' && event.propertyName !== 'opacity') return;
    cleanupClasses(element);
    element.removeEventListener('transitionend', onEnd);
  };

  element.addEventListener('transitionend', onEnd);

  // Fallback in case transitionend never fires (hidden tab, motion preference
  // changed mid-flight, etc.).
  window.setTimeout(() => {
    cleanupClasses(element);
    element.removeEventListener('transitionend', onEnd);
  }, 2500);
}

function initScrollReveal() {
  if (prefersReducedMotion()) return;
  if (document.documentElement.classList.contains('shopify-design-mode')) return;
  if (!('IntersectionObserver' in window)) return;

  const main = document.querySelector('main');
  if (!main) return;

  /** @type {Set<Element>} */
  const targets = new Set();

  // Auto-reveal whole sections. The first section (usually the announcement bar
  // or hero) is already in view and animated by its own components, and sections
  // that opt into their own reveal targets handle themselves.
  const sections = main.querySelectorAll(':scope > .shopify-section');
  sections.forEach((section, index) => {
    if (index === 0) return;
    if (section.querySelector('[data-reveal], [data-reveal-stagger]')) return;
    // Fixed-position descendants (e.g. the sticky add-to-cart bar) must not sit
    // inside a transformed ancestor or they stop being fixed.
    if (section.querySelector('.sticky-add-to-cart__bar')) return;
    targets.add(section);
  });

  // Explicit opt-in targets.
  main.querySelectorAll('[data-reveal]').forEach((element) => targets.add(element));

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        reveal(entry.target);
        observer.unobserve(entry.target);
      });
    },
    { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
  );

  targets.forEach((element) => {
    if (!isBelowFold(element)) return;
    element.classList.add(HIDDEN_CLASS);
    observer.observe(element);
  });

  // Staggered groups reveal their children in sequence.
  main.querySelectorAll('[data-reveal-stagger]').forEach((group) => {
    const children = Array.from(group.children).filter((child) => child instanceof HTMLElement);
    if (!children.length) return;

    const hiddenChildren = children.filter(isBelowFold);
    if (!hiddenChildren.length) return;

    hiddenChildren.forEach((child, index) => {
      child.style.setProperty('--reveal-delay', `${index * STAGGER_DELAY}s`);
      child.classList.add(HIDDEN_CLASS);
    });

    const groupObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          hiddenChildren.forEach(reveal);
          groupObserver.disconnect();
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -8% 0px' }
    );
    groupObserver.observe(group);
  });
}

onDocumentReady(initScrollReveal);